'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useAuth } from '@/components/AuthProvider';
import { leaveKindMeta } from '@/lib/leave';

interface PendingItem {
  id: number;
  user_name: string;
  user_email: string;
  kind: string;
  start_date: string;
  end_date: string;
  created_at: string;
}

const POLL_MS = 60_000;

export default function LeaveNotifications() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('manage.leave');

  const [items, setItems] = useState<PendingItem[]>([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/notifications/leave', { cache: 'no-store' });
      if (!res.ok) return;
      const data = await res.json();
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch { /* silencioso: la alerta es best-effort */ }
  }, []);

  useEffect(() => {
    if (!canManage) return;
    load();
    const id = setInterval(load, POLL_MS);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, [canManage, load]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (!canManage || items.length === 0) return null;

  const count = items.length;

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <button
        type="button"
        className="notif-pulse"
        onClick={() => setOpen(o => !o)}
        title={`${count} ausencia${count === 1 ? '' : 's'} por autorizar`}
        aria-label={`${count} ausencias por autorizar`}
        style={{
          position: 'relative',
          background: 'rgba(245, 255, 0, 0.08)',
          border: '1px solid var(--neon-yellow)',
          borderRadius: '8px',
          color: 'var(--neon-yellow)',
          padding: '0.4rem 0.6rem',
          fontSize: '1rem',
          cursor: 'pointer',
          lineHeight: 1,
        }}
      >
        🔔
        <span style={{
          position: 'absolute', top: '-7px', right: '-7px',
          background: 'var(--neon-red)', color: '#fff',
          fontSize: '0.62rem', fontWeight: 800, fontFamily: 'var(--font-mono)',
          minWidth: '17px', height: '17px', borderRadius: '9px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '0 4px', boxShadow: '0 0 6px rgba(255, 0, 64, 0.7)',
        }}>
          {count > 99 ? '99+' : count}
        </span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', right: 0, top: 'calc(100% + 0.5rem)',
          width: 'min(360px, 90vw)', zIndex: 200,
          background: 'var(--bg-card)',
          border: '1px solid rgba(245, 255, 0, 0.4)',
          borderRadius: '10px',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5), 0 0 12px rgba(245, 255, 0, 0.25)',
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '0.6rem 0.85rem', borderBottom: '1px solid var(--border-color)',
            fontSize: '0.78rem', fontWeight: 700, color: 'var(--neon-yellow)',
            display: 'flex', alignItems: 'center', gap: '0.4rem',
          }}>
            🔔 Ausencias por autorizar ({count})
          </div>
          <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
            {items.map(it => {
              const km = leaveKindMeta(it.kind);
              return (
                <Link
                  key={it.id}
                  href={`/admin/leave-requests/${it.id}`}
                  onClick={() => setOpen(false)}
                  style={{
                    display: 'block', padding: '0.6rem 0.85rem',
                    borderBottom: '1px solid var(--border-color)',
                    textDecoration: 'none', color: 'var(--text-primary)',
                  }}
                >
                  <div style={{ fontSize: '0.82rem', fontWeight: 600 }}>
                    {it.user_name || it.user_email}
                  </div>
                  <div style={{ fontSize: '0.74rem', color: 'var(--text-secondary)', marginTop: '0.15rem' }}>
                    {km.emoji} {km.label}
                    <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginLeft: '0.4rem' }}>
                      {String(it.start_date).slice(0, 10)} → {String(it.end_date).slice(0, 10)}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
          <Link
            href="/admin/leave-requests"
            onClick={() => setOpen(false)}
            style={{
              display: 'block', padding: '0.55rem 0.85rem', textAlign: 'center',
              fontSize: '0.78rem', fontWeight: 700, color: 'var(--neon-cyan)',
              textDecoration: 'none', background: 'rgba(0, 245, 255, 0.06)',
            }}
          >
            Ver todas las solicitudes →
          </Link>
        </div>
      )}
    </div>
  );
}
